import { buildSelectedSeatPayload } from './render';
import type { SectorPathData } from './svgParser';
import type {
  BuildPriceHighlightedLodgeSectorNamesParams,
  SeatData,
  SelectedSeat,
} from '../models/types';

export const isLodgeSectorName = (
  sectorName: string | null | undefined,
  normalizeSectorName: (name?: string) => string
) => !!sectorName && normalizeSectorName(sectorName).toLowerCase().includes('лож');

export const hasClosedSectorArea = (path: string, type: 'path' | 'rect') => {
  if (type === 'rect') return true;
  return /(?:z)\s*$/i.test(path.trim());
};

export const isRoyalVipFamilySector = (sectorName: string | null | undefined): boolean => {
  if (!sectorName) return false;
  const normalizedRaw = String(sectorName)
    .replace(/[–—_-]/g, ' ')
    .replace(/\s*\(.*?\)\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  return /^royal vip(?: \d+)?$/.test(normalizedRaw);
};

export const buildPriceHighlightedLodgeSectorNames = ({
  seatsInfoData,
  activePrices,
  normalizeSectorName,
  normalizedSectorPathMap,
}: BuildPriceHighlightedLodgeSectorNamesParams): Set<string> => {
  const highlighted = new Set<string>();
  if (activePrices.length === 0) return highlighted;

  const activePriceSet = new Set(activePrices);
  for (const seat of seatsInfoData) {
    if (!activePriceSet.has(seat.price)) continue;
    if (
      !isLodgeSectorName(seat.sector, normalizeSectorName) &&
      !isRoyalVipFamilySector(seat.sector)
    ) {
      continue;
    }

    const sectorName = normalizedSectorPathMap.get(normalizeSectorName(seat.sector));
    if (sectorName) highlighted.add(sectorName);
  }

  return highlighted;
};

export const buildLodgeSectorsWithSeats = (
  seatsInfoData: SeatData[],
  normalizeSectorName: (name?: string) => string,
  isLodgeSector: (sectorName: string | null | undefined) => boolean
) => {
  const sectors = new Set<string>();
  seatsInfoData.forEach(seatInfo => {
    if (isLodgeSector(seatInfo.sector) || isRoyalVipFamilySector(seatInfo.sector)) {
      sectors.add(normalizeSectorName(seatInfo.sector));
    }
  });
  sectors.add(normalizeSectorName('Royal VIP'));
  sectors.add(normalizeSectorName('Royal VIP2'));
  return sectors;
};

export const buildSelectedLodgeSectorNames = ({
  selectedSeats,
  seatIdToSectorName,
  isLodgeSector,
  normalizedSectorPathMap,
  normalizeSectorName,
}: {
  selectedSeats: SelectedSeat[];
  seatIdToSectorName: Map<number, string>;
  isLodgeSector: (sectorName: string | null | undefined) => boolean;
  normalizedSectorPathMap: Map<string, string>;
  normalizeSectorName: (name?: string) => string;
}) => {
  const selected = new Set<string>();
  selectedSeats.forEach(seat => {
    const sectorName = seatIdToSectorName.get(seat.seatId);
    if (!sectorName) return;
    if (!isLodgeSector(sectorName) && !isRoyalVipFamilySector(sectorName)) return;
    const matchedSectorName = normalizedSectorPathMap.get(normalizeSectorName(sectorName));
    if (matchedSectorName) {
      selected.add(matchedSectorName);
    }
  });
  return selected;
};

export const buildLodgeSelectionPayloadBySectorName = ({
  seatsInfoData,
  isLodgeSector,
  normalizedSectorPathMap,
  normalizeSectorName,
  eventId,
}: {
  seatsInfoData: SeatData[];
  isLodgeSector: (sectorName: string | null | undefined) => boolean;
  normalizedSectorPathMap: Map<string, string>;
  normalizeSectorName: (name?: string) => string;
  eventId: number;
}) => {
  const map = new Map<string, ReturnType<typeof buildSelectedSeatPayload>>();
  seatsInfoData.forEach(seatInfo => {
    if (!isLodgeSector(seatInfo.sector) && !isRoyalVipFamilySector(seatInfo.sector)) return;
    const canonicalSectorName =
      normalizedSectorPathMap.get(normalizeSectorName(seatInfo.sector)) ?? seatInfo.sector;
    if (map.has(canonicalSectorName)) return;
    map.set(
      canonicalSectorName,
      buildSelectedSeatPayload(
        {
          id: `lodge-${canonicalSectorName}`,
          sectorName: seatInfo.sector,
          row: 0,
          seat: 0,
          x: 0,
          y: 0,
          price: seatInfo.price,
          isAvailable: true,
        },
        {
          id: seatInfo.id,
          price: seatInfo.price,
          sectorName: seatInfo.sector,
          row: seatInfo.row ?? seatInfo.rowName ?? '',
          seat: seatInfo.seat ?? seatInfo.seatName ?? '',
          groupKey: '',
        },
        eventId
      )
    );
  });
  return map;
};

export const buildVisibleSectorNames = ({
  hoveredSectorName,
  selectedLodgeSectorNames,
  sectorPaths,
  lodgeSectorsWithSeats,
  normalizeSectorName,
}: {
  hoveredSectorName: string | null;
  selectedLodgeSectorNames: Set<string>;
  sectorPaths: SectorPathData[];
  lodgeSectorsWithSeats: Set<string>;
  normalizeSectorName: (name?: string) => string;
}) => {
  const names = new Set<string>();
  if (hoveredSectorName) {
    names.add(hoveredSectorName);
  }
  selectedLodgeSectorNames.forEach(name => {
    names.add(name);
  });
  sectorPaths.forEach(sector => {
    if (lodgeSectorsWithSeats.has(normalizeSectorName(sector.name))) {
      names.add(sector.name);
    }
  });
  return names;
};
